import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Container,
  CircularProgress
} from '@mui/material';
import {
  CloudOff,
  Refresh,
  Schedule,
  WifiOff
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../../stores/errorStore';
import { CriticalErrorType } from '../../types/errors';
import { useSystem } from '../../hooks/api/config';
import { log } from '../../utils/errors/logger';
import { formatTime } from '../../utils/formatting/formatters';

interface CriticalErrorPageProps {
  type?: CriticalErrorType;
  since?: number;
  onRetry?: () => void;
}

const ERROR_CONFIGS = {
  backend_down: {
    title: 'Server nicht erreichbar',
    description: 'Der Server ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.',
    icon: CloudOff,
    color: 'error' as const
  },
  network_failure: {
    title: 'Netzwerkfehler',
    description: 'Keine Verbindung zum Server möglich. Bitte überprüfen Sie Ihre Internetverbindung.',
    icon: WifiOff,
    color: 'warning' as const
  },
  total_failure: {
    title: 'Systemfehler',
    description: 'Ein kritischer Systemfehler ist aufgetreten.',
    icon: CloudOff,
    color: 'error' as const
  }
};

export const CriticalErrorPage: React.FC<CriticalErrorPageProps> = ({
  type = 'backend_down',
  since,
  onRetry
}) => {
  const { t, i18n } = useTranslation();
  const errorHandler = useErrorHandler();
  const { system } = useSystem();
  const [isRetrying, setIsRetrying] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);

  const errorConfig = ERROR_CONFIGS[type] || ERROR_CONFIGS.backend_down;
  const IconComponent = errorConfig.icon;

  // Calculate downtime duration
  const downtimeDuration = since ? Math.floor((Date.now() - since) / 1000) : 0;
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    log.user.action('critical_error_retry', 'CriticalErrorPage', { 
      type, 
      retryCount: retryCount + 1 
    });
    
    try {
      // Attempt health check
      const isHealthy = await errorHandler.checkHealth();
      
      if (isHealthy) {
        log.user.action('critical_error_recovery', 'CriticalErrorPage');
        errorHandler.clearCriticalError();
        
        if (onRetry) {
          onRetry();
        } else {
          // Force page refresh to reset state
          window.location.reload();
        }
      }
    } catch (error) {
      log.error('Critical error retry failed', { error });
    } finally {
      setIsRetrying(false);
    }
  };

  // Get system branding with fallbacks
  const appTitle = system?.name || 'SmartDoc';
  const logoUrl = system?.settings?.branding?.logo_url || '/logo-de.png';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Same background gradients as login page
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(135deg, 
              rgba(0, 48, 100, 0.04) 0%, 
              rgba(255, 255, 255, 0.85) 30%,
              rgba(255, 255, 255, 0.85) 70%,
              rgba(225, 158, 25, 0.04) 100%
            ),
            radial-gradient(ellipse at top left, rgba(0, 48, 100, 0.03) 0%, transparent 50%),
            radial-gradient(ellipse at bottom right, rgba(225, 158, 25, 0.02) 0%, transparent 50%)
          `,
          zIndex: 2,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            rgba(255, 255, 255, 0.1),
            repeating-linear-gradient(
              45deg,
              transparent,
              transparent 120px,
              rgba(0, 48, 100, 0.008) 120px,
              rgba(0, 48, 100, 0.008) 122px
            )
          `,
          zIndex: -1,
        }
      }}
    >
      {/* Background Video - same as login page */}
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          opacity: 0.4,
        }}
      >
        <source src="/Titelvideo_CPRO_Homepage_.mp4" type="video/mp4" />
      </Box>

      {/* Error content container - same positioning as login form */}
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              width: '100%',
              background: 'rgba(255, 255, 255, 0.35)',
              backdropFilter: 'blur(15px) saturate(180%)',
              WebkitBackdropFilter: 'blur(15px) saturate(180%)',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 20px 40px rgba(0, 48, 100, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              // Top gradient bar - error color instead of brand colors
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: errorConfig.color === 'error' 
                  ? 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' 
                  : 'linear-gradient(90deg, #ed6c02 0%, #ff9800 100%)',
              }
            }}
          >
            {/* System Logo */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={logoUrl}
                alt={`${appTitle} Logo`}
                sx={{
                  maxWidth: '80%',
                  height: 'auto',
                  maxHeight: '60px',
                  opacity: 0.7, // Slightly faded in error state
                }}
              />
            </Box>

            {/* Error Icon and Title */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <IconComponent 
                sx={{ 
                  fontSize: 64, 
                  color: errorConfig.color === 'error' ? 'error.main' : 'warning.main',
                  mb: 2
                }}
              />
              
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  color: '#003064',
                  fontWeight: 600,
                  mb: 1
                }}
              >
                {errorConfig.title}
              </Typography>
              
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'rgba(0, 48, 100, 0.8)',
                  opacity: 0.9
                }}
              >
                {errorConfig.description}
              </Typography>
            </Box>

            {/* Status chips */}
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
              <Chip
                icon={<CloudOff />}
                label={`Status: ${errorHandler.backendStatus === 'online' ? 'Online' : 'Offline'}`}
                color={errorHandler.backendStatus === 'online' ? 'success' : 'error'}
                variant="outlined"
                size="small"
              />
              
              {since && (
                <Chip
                  icon={<Schedule />}
                  label={`Seit: ${formatDuration(downtimeDuration)}`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>

            {/* Error Alert */}
            <Alert 
              severity={errorConfig.color} 
              sx={{ mb: 3 }}
            >
              <Typography variant="body2">
                {t('error.critical.info', 
                  'Das System kann momentan nicht genutzt werden. Dies kann durch Wartungsarbeiten oder technische Probleme verursacht werden. Bitte versuchen Sie es in einigen Minuten erneut.'
                )}
              </Typography>
            </Alert>

            {/* Progress Bar when retrying */}
            {isRetrying && (
              <Box sx={{ mb: 3 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {t('error.critical.checking', 'Verbindung wird überprüft...')}
                </Typography>
              </Box>
            )}

            {/* Action Button */}
            <Button
              fullWidth
              variant="contained"
              startIcon={isRetrying ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              onClick={handleRetry}
              disabled={isRetrying}
              size="large"
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #003064 30%, #004080 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #002040 30%, #003064 90%)',
                }
              }}
            >
              {isRetrying 
                ? t('error.critical.checking', 'Überprüfe Verbindung...')
                : t('error.critical.retry', 'Erneut versuchen')
              }
            </Button>

            {/* Help text */}
            <Typography 
              variant="caption" 
              sx={{ 
                mt: 2, 
                display: 'block', 
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              {retryCount > 2 && t('error.critical.persistentIssue', 
                'Falls das Problem weiterhin besteht, kontaktieren Sie bitte den Support.'
              )}
            </Typography>

            {/* Debug info for development */}
            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Debug: Type={type}, Status={errorHandler.backendStatus},
                  Errors={errorHandler.criticalErrorCount}, Since={since ? formatTime(since, i18n.language) : 'N/A'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

// Auto-mounting version that shows when critical error is active
export const AutoCriticalErrorPage: React.FC<Omit<CriticalErrorPageProps, 'type' | 'since'>> = (props) => {
  const errorHandler = useErrorHandler();

  // Don't render if no critical error
  if (!errorHandler.hasCriticalError) {
    return null;
  }

  return (
    <CriticalErrorPage
      type={errorHandler.criticalError!}
      since={errorHandler.criticalErrorSince || undefined}
      {...props}
    />
  );
};